import { MinTableKey } from "./table";
import { TodoData } from "./todo";

// `invite#{sha256hex(token)}` record. expiresAt is epoch seconds.
export type InviteData = {
  type: "invite";
  ownerId: string;
  parentId: string;
  createdAt: string; // ISO
  expiresAt: number;
  createdBy: string;
  // The parent todo's createdAt (part of its SK), copied to the share so the
  // shared list can fetch the title. Absent on invites made before this field.
  parentCreatedAt?: string;
};
export type InviteDBData = MinTableKey & InviteData;

// In the invitee's partition: "you can read this owner's project".
export type ShareData = {
  type: "share";
  ownerId: string;
  parentId: string;
  inviteeId: string;
  createdAt: string; // ISO
  parentCreatedAt?: string;
};
export type ShareDBData = MinTableKey & ShareData;

// In the owner's partition: who was given access (for the owner's list).
export type GrantData = {
  type: "grant";
  ownerId: string;
  parentId: string;
  inviteeId: string;
  createdAt: string; // ISO
};
export type GrantDBData = MinTableKey & GrantData;

export type CreateInviteInput = {
  parentId: string;
  parentCreatedAt: string; // part of the todo's SK, which the client always has
};

// The token travels in the `{id}` path segment.
export type AcceptInviteInput = {
  id: string;
};

export type CreateInviteResponse = { token: string };
export type AcceptInviteResponse = { ownerId: string; parentId: string };

// GET /api/shared route name (not a stored SK prefix).
export const SHARED_ROUTE = "shared";
// GET /api/my-feedback: the caller's own feedback, with a mapped status.
export const MY_FEEDBACK_ROUTE = "my-feedback";
// GET /api/release-notes: public notes of tagged releases, for any signed-in user.
export const RELEASE_NOTES_ROUTE = "release-notes";
export type SharedProjectResponse = { parent: TodoData; steps: TodoData[] };
// A share as listed to its invitee: the project's title when it could be read.
export type SharedListItem = ShareData & { title?: string };
export type GetSharesResponse = { shares: SharedListItem[] };
export type GetGrantsResponse = { grants: GrantData[] };
// Ids that end up inside sort keys: no `#` or `_`, so keys can't alias.
export const isSafeId = (v: unknown): v is string =>
  typeof v === "string" && /^[A-Za-z0-9-]{1,64}$/.test(v);
// Invite tokens are base64url of 32 random bytes: 43 chars of A-Z a-z 0-9 - _.
// `_` is allowed here (unlike isSafeId) because the token is only ever the
// last segment of a key.
export const INVITE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
export const isSafeToken = (v: unknown): v is string =>
  typeof v === "string" && INVITE_TOKEN_PATTERN.test(v);
// A todo's createdAt as it appears in its SK: an ISO timestamp, no `#` or `_`.
export const isSafeCreatedAt = (v: unknown): v is string =>
  typeof v === "string" && /^[0-9TZ:.+-]{10,40}$/.test(v);
